import { z } from 'zod';
import { TMDXFile } from '@/api/apis/files';
import { getNameValuesFromEnumNames, getPhoneSchema } from '@/utils/helpers';
import { CreateTXUserFormValues, TXUserBaseFormType, TXUserFormValues, UpdateTXUserFormValues } from '@/types/customer';
import { CUSTOMER_PERMISSION_NAMES, CUSTOMER_ROLE_ADMIN_NAMES } from '@/constants/customer.constants';
import { CustomerPermissionEnum, CustomerRole, CustomerRoleEnum } from '@/api/apis/customer';
import { TXCenter } from '@/api/apis/facilities';
import { generalTxUserCreateValidation, generalTxUserUpdateValidation } from './utils';
import { SelectBooleanItemType } from '../types';

export const CUSTOMER_ROLES_SELECT_LIST = [
  ...getNameValuesFromEnumNames(CUSTOMER_ROLE_ADMIN_NAMES),
  ...getNameValuesFromEnumNames(CUSTOMER_PERMISSION_NAMES),
].sort((a, b) => a.name.localeCompare(b.name));

export const BASE_TX_USER_FORM_DEFAULT_VALUES: TXUserBaseFormType = {
  id: '',
  firstName: '',
  lastName: '',
  email: '',
  primaryPhone: '',
  secondaryPhone: '',
  photoPath: '',
  enabled: false,
};

export const CREATE_TX_USER_FORM_DEFAULT_VALUES: CreateTXUserFormValues = {
  ...BASE_TX_USER_FORM_DEFAULT_VALUES,
  roles: [],
  permissions: [],
  txCenterId: '',
  txCenterName: '',
  allowedEmailDomains: [],
};

export const UPDATE_TX_USER_FORM_DEFAULT_VALUES: UpdateTXUserFormValues = {
  ...BASE_TX_USER_FORM_DEFAULT_VALUES,
  id: '',
};

export const txUserSchemaBase = z.object({
  firstName: z.string().min(1, 'Provide first name'),
  lastName: z.string().min(1, 'Provide last name'),
  photo: z.custom<TMDXFile>().optional(),
  photoPath: z.string().optional(),
  email: z.email({ error: 'Invalid email' }),
  primaryPhone: z.string(),
  secondaryPhone: getPhoneSchema({ isRequired: false }),
});

export const createTxUserSchema = txUserSchemaBase
  .and(
    z.object({
      roles: z.enum(CustomerRoleEnum, { error: 'Invalid Role' }).array().nonempty('Provide role'),
      permissions: z.enum(CustomerPermissionEnum, { error: 'Invalid Permission' }).array(),
      txCenterName: z.string().min(1, 'Provide tx center'),
      txCenterId: z.uuid('Provide tx center'),
      allowedEmailDomains: z.array(z.string()).optional(),
    }),
  )
  .superRefine(generalTxUserCreateValidation);

export const updateTxUserSchema = txUserSchemaBase
  .and(
    z.object({
      id: z.uuid(),
      enabled: z.boolean(),
      customerInTxCenters: z.array(
        z.object({
          txCenterId: z.uuid('Provide tx center ID'),
          txCenter: z.custom<TXCenter>().optional(),
          roles: z.enum(CustomerRole, { error: 'Invalid Role' }).array().nonempty('Provide role'),
          isPrimary: z.boolean(),
        }),
      ),
    }),
  )
  .superRefine(generalTxUserUpdateValidation);

export const TX_USER_FORM_DEFAULT_VALUES: TXUserFormValues = {
  id: '',
  firstName: '',
  lastName: '',
  email: '',
  primaryPhone: '',
  secondaryPhone: '',
  roles: [],
  permissions: [],
  enabled: true,
  photoPath: '',
  txCenterId: '',
  txCenterName: '',
};

export const txUserSchema = z.object({
  firstName: z.string().min(1, 'Provide first name'),
  lastName: z.string().min(1, 'Provide last name'),
  photo: z.custom<TMDXFile>().optional(),
  photoPath: z.string().optional(),
  email: z.email({ error: 'Invalid email' }),
  primaryPhone: getPhoneSchema({ isRequired: true }),
  secondaryPhone: getPhoneSchema({ isRequired: false }),
  roles: z.enum(CustomerRole, { error: 'Invalid Role' }).array().nonempty('Provide role'),
  permissions: z.enum(CustomerRole, { error: 'Invalid Permissions' }).array(),
  enabled: z.boolean().optional(),
  txCenterId: z.uuid('Provide tx center'),
  txCenterName: z.string().min(1, 'Provide tx center name'),
});

export const IS_ACTIVE_TXUSER: SelectBooleanItemType[] = [
  { value: true, name: 'Yes' },
  { value: false, name: 'No' },
];
